/* eslint-disable react/no-array-index-key */
/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react/display-name */
/* eslint-disable consistent-return */
import React, { memo, useState, useCallback, useRef, useEffect } from 'react';
import ItemsSwiper from '@wconcept/wconcept-ui-renewal/src/components/atoms/ItemsSwiper';
import useInView from '@wconcept/wconcept-ui-renewal/hooks/useInView';
import BasicBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/BasicBtn';
import ContTitle from '@wconcept/wconcept-ui-renewal/components/molecules/_title/ContTitle';
import ShortformItemSkeleton from '@wconcept/wconcept-ui-renewal/components/molecules/_skeleton/ShortformItemSkeleton';
import { Swiper as SwiperClass } from 'swiper/types';
import SwiperNavigation from '@wconcept/wconcept-ui-renewal/components/molecules/SwiperNavigation';
import {
  getUniqueKey,
  classNameBind,
} from '@wconcept/wconcept-common/utils/commonUtils';
import * as S from './style';

interface CustomColorType {
  bgColor?: string;
  textColor?: string;
}

type ColorType = 'white' | 'black' | CustomColorType | string;

interface IProps {
  title?: string;
  subTitle?: string;
  items?: React.ReactNode;
  isPaused?: boolean;
  colorType?: ColorType;
  isDesktop?: boolean;
  isLoaded?: boolean;
  onSlideChange?: (swiper: SwiperClass) => void;
  onClickMore?: () => void;
}

interface VideoChildProps {
  isActive?: boolean;
  itemRef?: React.RefObject<HTMLDivElement | null>;
  videoPoster?: string;
  isLastItem?: boolean;
  isDesktop?: boolean;
  isLastInGroup?: boolean;
  isPaused?: boolean;
}

interface ObservedItemProps {
  children: React.ReactElement<VideoChildProps>;
  shouldObserve: boolean;
  index: number;
  activeIndex: number;
  onVideoEnd: () => void;
  isLastItem: boolean;
  isDesktop?: boolean;
  isPaused?: boolean;
}

// ObservedItem 컴포넌트: Intersection Observer를 사용하여 현재 화면에 보이는 아이템을 감지
const ObservedItem = memo(
  ({
    children,
    shouldObserve,
    index,
    activeIndex,
    onVideoEnd,
    isLastItem,
    isDesktop,
    isPaused,
  }: ObservedItemProps) => {
    const [ref, isVisible] = useInView(
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.5,
      },
      shouldObserve
    );

    return (
      <>
        {React.cloneElement(children, {
          // 현재 활성화된 아이템에만 isActive prop을 true로 전달
          isActive: isVisible && index === activeIndex,
          itemRef: ref,
          isLastItem,
          isDesktop,
          isPaused,
          ...(index === activeIndex && { onVideoEnd }),
        })}
      </>
    );
  }
);

const ShortformSection = ({
  title = '이렇게 연출해 보세요',
  subTitle,
  items,
  isPaused = false,
  colorType = 'white',
  onSlideChange,
  onClickMore,
  isDesktop,
  isLoaded,
}: IProps) => {
  const isCustomColor = typeof colorType === 'object';
  const customColors = isCustomColor ? colorType : undefined;

  const cName = classNameBind([
    'shortform-list',
    !isCustomColor ? `bg-type-${colorType}` : '',
  ]);

  const itemsLength = Array.isArray(items) ? items.length : 0;

  const swiperRef = useRef<SwiperClass>(null);

  const [shouldObserve, setShouldObserve] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const uniqueKeyNavi = `swiper-navigation-${getUniqueKey()}`;

  // 슬라이드 변경 시 처리하는 함수
  const handleSlideChange = useCallback(
    (swiper: SwiperClass) => {
      setActiveIndex(swiper.activeIndex);
      onSlideChange?.(swiper);
    },
    [onSlideChange]
  );

  /**
   * 비디오 재생이 끝났을 때 실행되는 핸들러
   *
   * Desktop 모드:
   * 1. isPaused가 false일 때만 자동 스와이프 진행
   * 2. swiper-slide-active를 기준으로 뒤에 있는 아이템들을 순차 재생
   * 3. active 슬라이드부터 최대 4개까지 재생 (현재 그룹 내에서)
   * 4. 그룹의 마지막에 도달하면 현재 그룹의 첫 번째 아이템으로 돌아감
   *
   * Mobile 모드:
   * 1. 현재 슬라이드 영상을 무한 반복 재생
   * 2. isPaused 상태에 따라 pause/play 제어
   */
  const handleVideoEnd = useCallback(() => {
    if (swiperRef.current) {
      const effectiveLastIndex = itemsLength - 1;

      if (isDesktop) {
        // 데스크톱: isPaused가 false일 때만 자동 스와이프
        if (isPaused) {
          // pause 상태일 때는 현재 activeIndex 유지
          setActiveIndex(activeIndex);
          return;
        }

        // 데스크톱: 현재 보이는 슬라이드 그룹 내에서 순차 재생
        const currentActiveSlideIndex = swiperRef.current.activeIndex;
        const remainingItems = effectiveLastIndex - currentActiveSlideIndex;

        // 현재 그룹에서 재생 가능한 마지막 인덱스 계산 (최대 4개)
        const playableEndIndex = Math.min(
          currentActiveSlideIndex + Math.min(3, remainingItems),
          effectiveLastIndex
        );

        if (activeIndex < playableEndIndex) {
          // 재생 가능한 범위 내에서 다음 아이템으로 이동
          setActiveIndex(activeIndex + 1);
        } else {
          // 그룹의 마지막에 도달했을 때
          // 현재 그룹에 아이템이 하나만 있는지 확인
          const itemsInCurrentGroup =
            playableEndIndex - currentActiveSlideIndex + 1;

          if (itemsInCurrentGroup === 1) {
            // 그룹 내에 아이템이 하나만 있는 경우: activeIndex를 유지하여 반복 재생
            setActiveIndex(activeIndex);
          } else {
            // 그룹에 여러 아이템이 있는 경우: 그룹의 첫 번째 아이템으로 돌아감
            setActiveIndex(currentActiveSlideIndex);
          }
        }
      } else {
        // 모바일: 현재 슬라이드에서 무한 반복 재생
        setActiveIndex(activeIndex);
      }
    }
  }, [activeIndex, itemsLength, isDesktop, isPaused]);

  useEffect(() => {
    setTimeout(() => {
      setShouldObserve(true);
    }, 100);
  }, []);

  /**
   * 각 아이템을 ObservedItem으로 감싸서 화면 노출 여부를 추적하는 함수
   * - Intersection Observer를 통해 현재 화면에 보이는 아이템만 활성화
   * - Desktop에서는 그룹 내 마지막 아이템 여부를 판단하여 isLastInGroup 전달
   * - isPaused prop을 모든 아이템에 전달
   */
  const getListRender = useCallback(
    (el: React.ReactNode) => {
      if (Array.isArray(el)) {
        const effectiveLastIndex = itemsLength - 1;
        return el
          .map((v: React.ReactElement, i) => {
            if (v) {
              // Desktop에서 그룹 내 마지막 아이템인지 확인
              const isLastInGroup =
                isDesktop &&
                (() => {
                  if (!swiperRef.current) return false;
                  const currentActiveSlideIndex = swiperRef.current.activeIndex;
                  const remainingItems =
                    effectiveLastIndex - currentActiveSlideIndex;
                  const playableEndIndex = Math.min(
                    currentActiveSlideIndex + Math.min(3, remainingItems),
                    effectiveLastIndex
                  );
                  const itemsInCurrentGroup =
                    playableEndIndex - currentActiveSlideIndex + 1;
                  return itemsInCurrentGroup === 1 && i === activeIndex;
                })();

              // 일반 컨텐츠 아이템들은 ObservedItem으로 감싸서 intersection observer 적용
              return (
                <ObservedItem
                  key={v.key || `item-${i}`}
                  shouldObserve={shouldObserve}
                  index={i}
                  activeIndex={activeIndex}
                  onVideoEnd={handleVideoEnd}
                  isLastItem={i === effectiveLastIndex}
                  isDesktop={isDesktop}
                  isPaused={isPaused}
                >
                  {React.cloneElement(
                    v as React.ReactElement<VideoChildProps>,
                    {
                      isLastInGroup,
                      isPaused,
                    }
                  )}
                </ObservedItem>
              );
            }
            return null;
          })
          .filter((item): item is React.ReactElement => item !== null);
      }
      return null;
    },
    [
      shouldObserve,
      activeIndex,
      handleVideoEnd,
      itemsLength,
      isDesktop,
      isPaused,
    ]
  );

  // 데스크톱 파라미터
  const desktopPara: any = {
    slidesPerView: 4,
    slidesPerGroup: 4,
    spaceBetween: 18,
    navigation: {
      prevEl: `.${uniqueKeyNavi} .swiper-button-prev`,
      nextEl: `.${uniqueKeyNavi} .swiper-button-next`,
    },
    navigationEl: (
      <SwiperNavigation className={uniqueKeyNavi} offsetTop={285.5} />
    ),
  };

  // 모바일 파라미터
  const mobilePara: any = {
    slidesPerView: 'auto',
    slidesOffsetAfter: 20,
    slidesOffsetBefore: 20,
    spaceBetween: 6,
    className: itemsLength <= 2 ? 'min' : '',
  };

  return (
    <S.ShortformSection
      className={cName}
      $bgColor={customColors?.bgColor}
      $textColor={customColors?.textColor}
    >
      <ContTitle
        title={title}
        subTitle={subTitle}
        titleColor={customColors?.textColor}
        subTitleColor={customColors?.textColor}
      />

      {isLoaded ? (
        <ItemsSwiper
          {...(isDesktop ? desktopPara : mobilePara)}
          loop={false}
          autoplay={false}
          onSlideChange={handleSlideChange}
          onSwiper={(swiper: SwiperClass) => {
            swiperRef.current = swiper;
          }}
        >
          {getListRender(items)}
        </ItemsSwiper>
      ) : (
        <div className="skeleton-list">
          {Array.from({ length: isDesktop ? 4 : 2 }).map((_, index) => (
            <ShortformItemSkeleton
              key={index}
              bgType={!isCustomColor ? colorType : 'white'}
            />
          ))}
        </div>
      )}

      <BasicBtn
        className="btn-section-more"
        text="숏폼 더보기"
        onClick={onClickMore}
      />
    </S.ShortformSection>
  );
};

export default memo(ShortformSection);
